import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, ArrowRight, MapPin } from 'lucide-react';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Postcode entry box — the highest-intent funnel on the UK landing.
 *
 * Accepts any UK postcode (full or outward-only), normalises it, and
 * routes to /uk/postcode/{OUTWARD}. Buyers who already know where they
 * want to live skip the region-tile grid entirely.
 *
 * Autocomplete: as the user types ≥1 character, we debounce-fetch
 * suggestions from `/api/uk/sold-prices/outwards?prefix=<X>` — distinct
 * outward postcodes that have HM Land Registry data, ordered by record
 * count. Suggestions are keyboard-navigable (↑/↓/Enter/Esc) and clicking
 * one submits straight through.
 *
 * Validation (lenient — we'd rather route a typo to an empty-state page
 * than reject a valid postcode the regex doesn't know about):
 *   • Min 2 chars, max 8
 *   • Must start with a letter and contain at least one digit
 *   • Outward = strip last 3 chars when length >= 5
 *
 * Props:
 *   variant   'hero' (light-on-dark) | 'inline' (dark-on-light). Default 'hero'.
 */
const UkPostcodeSearchBox = ({ variant = 'hero' }) => {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);
  const [value, setValue] = useState('');
  const [error, setError] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const isHero = variant === 'hero';

  const normalise = (raw) => {
    const cleaned = (raw || '').trim().toUpperCase().replace(/\s+/g, '');
    if (cleaned.length < 2) return null;
    if (!/^[A-Z]/.test(cleaned) || !/\d/.test(cleaned)) return null;
    return cleaned.length >= 5 ? cleaned.slice(0, -3) : cleaned;
  };

  const goToOutward = (outward) => {
    if (!outward) return;
    setShowDropdown(false);
    navigate(`/uk/postcode/${outward}`);
  };

  // Debounced suggestion fetch ─ short prefixes are cheap; we cap at 4.
  const fetchSuggestions = useCallback((prefix) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!prefix || prefix.length < 1) {
      setSuggestions([]);
      return;
    }
    debounceRef.current = setTimeout(() => {
      axios
        .get(`${BACKEND_URL}/api/uk/sold-prices/outwards`, {
          params: { prefix, limit: 6 },
        })
        .then((r) => {
          const rows = (r.data?.results || []).filter(
            (s) => s.outward && s.outward !== prefix
          );
          setSuggestions(rows);
          setActiveIndex(-1);
        })
        .catch(() => setSuggestions([]));
    }, 180);
  }, []);

  const onChange = (e) => {
    const raw = e.target.value;
    setValue(raw);
    if (error) setError('');
    const cleaned = raw.trim().toUpperCase().replace(/\s+/g, '');
    // Send the outward part only (max 4 chars) so the backend index
    // anchored-regex is cheap.
    fetchSuggestions(cleaned.slice(0, 4));
    setShowDropdown(true);
  };

  const handleSubmit = (e) => {
    e?.preventDefault?.();
    // If a suggestion is highlighted, prefer it.
    if (activeIndex >= 0 && suggestions[activeIndex]) {
      goToOutward(suggestions[activeIndex].outward);
      return;
    }
    const outward = normalise(value);
    if (!outward) {
      setError('Enter a valid UK postcode (e.g. SW1A or M20 6WD)');
      inputRef.current?.focus();
      return;
    }
    setError('');
    goToOutward(outward);
  };

  // ── Keyboard navigation ────────────────────────────────────────────
  const onKeyDown = (e) => {
    if (!showDropdown || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, -1));
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
      setActiveIndex(-1);
    }
  };

  // Close on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (!wrapperRef.current?.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  return (
    <form
      ref={wrapperRef}
      onSubmit={handleSubmit}
      data-testid="uk-postcode-search-box"
      className={`relative flex items-stretch gap-2 max-w-xl ${
        isHero ? 'mt-8' : 'mt-4'
      }`}
    >
      <div
        className={`relative flex-1 flex items-center gap-2 rounded-full px-5 py-3 ${
          isHero
            ? 'bg-white/10 backdrop-blur ring-1 ring-white/20 focus-within:ring-white/40'
            : 'bg-white ring-1 ring-gray-200 focus-within:ring-[#7042cc]'
        }`}
      >
        <Search
          className={`h-4 w-4 shrink-0 ${
            isHero ? 'text-white/60' : 'text-gray-400'
          }`}
        />
        <input
          ref={inputRef}
          type="text"
          inputMode="text"
          autoComplete="postal-code"
          spellCheck={false}
          maxLength={10}
          value={value}
          onChange={onChange}
          onKeyDown={onKeyDown}
          onFocus={() => value && setShowDropdown(true)}
          placeholder="Type any UK postcode — e.g. SW1A or M20 6WD"
          aria-label="UK postcode"
          aria-autocomplete="list"
          aria-controls="uk-postcode-suggestions"
          aria-expanded={showDropdown && suggestions.length > 0}
          data-testid="uk-postcode-input"
          className={`flex-1 bg-transparent border-0 outline-none text-base tabular-nums uppercase placeholder:normal-case placeholder:tracking-normal placeholder:text-sm placeholder:uppercase-none ${
            isHero
              ? 'text-white placeholder:text-white/40'
              : 'text-gray-900 placeholder:text-gray-400'
          }`}
        />
      </div>
      <Button
        type="submit"
        data-testid="uk-postcode-submit"
        className={`rounded-full px-5 sm:px-6 font-semibold ${
          isHero
            ? 'bg-white text-[#7042cc] hover:bg-[#fff7eb]'
            : 'bg-[#7042cc] text-white hover:bg-[#5b35a8]'
        }`}
      >
        <span className="hidden sm:inline mr-2">See sold prices</span>
        <ArrowRight className="h-4 w-4" />
      </Button>

      {/* Suggestions dropdown */}
      {showDropdown && suggestions.length > 0 && (
        <ul
          id="uk-postcode-suggestions"
          role="listbox"
          data-testid="uk-postcode-suggestions"
          className="absolute left-0 right-[110px] top-full mt-2 z-30 overflow-hidden rounded-xl bg-white text-gray-900 ring-1 ring-gray-200 shadow-lg max-h-72 overflow-y-auto"
        >
          {suggestions.map((s, i) => {
            const active = i === activeIndex;
            return (
              <li
                key={s.outward}
                role="option"
                aria-selected={active}
                data-testid={`uk-postcode-suggestion-${s.outward}`}
                onMouseEnter={() => setActiveIndex(i)}
                onMouseDown={(e) => {
                  // Use mouseDown to avoid losing focus before the click handler fires.
                  e.preventDefault();
                  goToOutward(s.outward);
                }}
                className={`flex items-center justify-between gap-3 px-4 py-2.5 cursor-pointer text-sm ${
                  active ? 'bg-[#7042cc]/10' : 'hover:bg-gray-50'
                }`}
              >
                <span className="inline-flex items-center gap-2">
                  <MapPin className="h-3.5 w-3.5 text-gray-400" />
                  <span className="font-mono font-bold tabular-nums">{s.outward}</span>
                </span>
                <span className="text-[11px] text-gray-500 tabular-nums">
                  {s.count} {s.count === 1 ? 'sale' : 'sales'}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {error && (
        <p
          role="alert"
          data-testid="uk-postcode-error"
          className={`absolute mt-14 text-xs ${
            isHero ? 'text-[#ffb37a]' : 'text-red-600'
          }`}
        >
          {error}
        </p>
      )}
    </form>
  );
};

export default UkPostcodeSearchBox;
